import { memo } from 'react';
import { Box, Button, SpaceBetween, TextContent } from '@amzn/awsui-components-core';

import { useBarRaiserStatePanelViewModel } from './BarRaiserStatePanelViewModel';
import { DuplicationResultsModal } from './DuplicationResultsModal';
import PanelSection from './PanelSection';
import ScanFindingsTable from './ScanFindingsTable';
import { ScanFailedAlert } from './ScanFailedAlert';
import { PanelDivider } from './PanelDivider';

const BarRaiserStatePanel = memo(function BarRaiserStatePanel() {
  const vm = useBarRaiserStatePanelViewModel();

  if (vm.isScanning || vm.isLoadingFindings) {
    return (
      <SpaceBetween size="m" direction="vertical" alignItems="center">
        {vm.isScanning && (
          <Box color="text-body-secondary">Scanning your content for quality issues…</Box>
        )}
        {vm.isLoadingFindings && (
          <Box color="text-body-secondary">Checking status of automated scanning…</Box>
        )}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m" direction="vertical">
      {vm.isScanFailed && (
        <ScanFailedAlert type="warning">
          The automated quality check could not be completed. Review the content manually before
          evaluating.
        </ScanFailedAlert>
      )}

      <ScanFindingsTable
        scannerFindings={vm.scannerFindings}
        isLoading={false}
        onViewDuplication={vm.openDuplicationModal}
      />

      {vm.duplicationModalVisible && (
        <DuplicationResultsModal
          matches={vm.duplicationModalMatches}
          onDismiss={vm.dismissDuplicationModal}
          onResultClick={vm.onDuplicationResultClick}
        />
      )}

      <PanelDivider />

      <PanelSection title="Required to proceed">
        {vm.subState === 'inProcess' && (
          <TextContent>
            <ol>
              <li>Confirm the code requirements are met.</li>
              <li>Review the potential duplicates.</li>
              <li>
                Dismiss any scan-generated comments that you don't want to send back to the author.
              </li>
              <li>(Optional) Add more comments for the author.</li>
              <li>Evaluate the publication quality to approve or reject the content.</li>
            </ol>
          </TextContent>
        )}
        {vm.subState === 'rejected' && (
          <TextContent>
            <ul>
              <li>Wait for author to resubmit</li>
            </ul>
          </TextContent>
        )}
      </PanelSection>

      {vm.subState === 'inProcess' && (
        <SpaceBetween size="xs" direction="vertical">
          <Button variant="primary" fullWidth onClick={vm.openEvaluationPanel}>
            Evaluate publication quality
          </Button>
          <Button variant="link" fullWidth onClick={vm.openCommentsPanel}>
            Review scan comments
          </Button>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
});

export default BarRaiserStatePanel;
